import { useCallback, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Form, { Field as BaseField } from "rc-field-form";
import Field from "../../../../../components/Field";
import Button from "../../../../../components/Button";
import { useProfileApi } from "../../../../../utils/api/profile";
import { usePeopleApi } from "../../../../../utils/api/people";
import { generateErrorsConfigForForm } from "../../../../../utils/helpers/errors";
import {
	getSettingsResourceSearchFilters,
	getSettingsResourceSearchFiltersWithLookups,
	renderSettingsResourceLabel,
} from "../../../../../utils/helpers/settings";
import { renderUserName } from "../../../../../utils/helpers/user";
import ManagersSelect from "../../../../../components/Inputs/ManagersSelect";
import ResourceSelect from "../../../../../components/Inputs/ResourceSelect";
import { ProfileContext } from "../../../context";
import CompensationSelect from "../../../../../components/Inputs/CompensationSelect";
import { useIsFieldDisabled } from "../../../useIsFieldDisabled";

export const getCompaniesSearchFilters = (search) => ({
	"filter[name][method]": "contains",
	"filter[name][value]": search,
	"filter[code][method]": "contains",
	"filter[code][value]": search,
	"filter[externalCompanyCode][method]": "contains",
	"filter[externalCompanyCode][value]": search,
	"filter[externalCompanyId][method]": "contains",
	"filter[externalCompanyId][value]": search,
	fields: ["id", "name", "code", "externalCompanyId", "externalCompanyCode"],
	filterType: "or",
	withoutLookups: "true",
});

const formatFormValues = (values) => {
	const formatLocation = ({ id, code, description, timezone }) => ({
		primary: values?.primaryLocation?.id === id,
		locationId: id,
		code,
		description,
		displayName: renderSettingsResourceLabel({
			code,
			description,
		}),
		timezoneId: timezone?.id,
		timezoneValue: timezone?.value,
		status: "active",
		shared: false,
		startDate: new Date(),
	});
	const locations = [values.primaryLocation, ...(values.otherLocations || [])];
	return {
		companies: values?.companies.map(({ id }) => id),
		payGroup: values?.payGroup?.id,
		calculationGroup: values?.calculationGroup?.id,
		salaryOrHourly: values?.salaryOrHourly,
		locations: locations.map(formatLocation),
		supervisors: values.supervisors?.map(
			({ id, firstName, middleName, lastName, employeeNumber, email }) => ({
				primary: values.primarySupervisor === id,
				firstName,
				lastName,
				email,
				employeeNumber,
				userId: id,
				fullName: renderUserName({
					firstName,
					middleName,
					lastName,
				}),
			}),
		),
	};
};

function ChangeCompanyInformation({ close }) {
	const { t } = useTranslation();

	const [form] = Form.useForm();

	const { user, submitting, submittingError, submit, isSelf } =
		useContext(ProfileContext);

	const { changeCompanyInformation: profileSubmit } = useProfileApi();
	const { changeCompanyInformation: peopleSubmit } = usePeopleApi();
	const getIsFieldDisabled = useIsFieldDisabled("peopleApiConnection.companyInformation");

	const onFinish = useCallback(
		(values) => {
			const data = formatFormValues(values);
			submit(profileSubmit, peopleSubmit, data, close);
		},
		[submit, profileSubmit, peopleSubmit, close],
	);

	useEffect(() => {
		const formatLocation = (location) => {
			return {
				id: location.locationId,
				code: location.code,
				description: location.description,
				timezone: {
					id: location.timezoneId,
					value: location.timezoneValue,
				},
			};
		}
		const primaryLocation = user?.locations.find(({ primary }) => primary);
		const otherLocations = user?.locations.filter(({ primary }) => !primary) || [];
		form.setFieldsValue({
			companies: user?.companies,
			payGroup: user?.payGroup,
			salaryOrHourly: user?.salaryOrHourly,
			primaryLocation: primaryLocation && formatLocation(primaryLocation),
			otherLocations: otherLocations.map(formatLocation),
			supervisors: user?.supervisors?.map(({ userId, ...user }) => ({
				...user,
				id: userId,
			})),
			primarySupervisor: user?.supervisors?.find(({ primary }) => primary)
				?.userId,
			calculationGroup: user?.calculationGroup,
		});
	}, [form, user]);

	useEffect(() => {
		const fieldErrors = generateErrorsConfigForForm(
			["companies", "locations", "payGroup"],
			submittingError,
		);
		form.setFields(fieldErrors);
	}, [form, submittingError]);

	return (
		<Form
			form={form}
			layout="vertical"
			onFinish={onFinish}
			scrollToFirstError
		>
			<Field
				name="companies"
				label={t("companies")}
				rules={[
					{
						required: !getIsFieldDisabled("companies"),
						message: t("required-companies"),
					},
				]}
			>
				<ResourceSelect
					labelPropName="name"
					resourcePath="/companies"
					placeholder={t("companies")}
					hasSearch
					getSearchFilters={getCompaniesSearchFilters}
					mode="multiple"
					disabled={getIsFieldDisabled("companies")}
				/>
			</Field>

			<ManagersSelect
				form={form}
				isSelf={isSelf}
				sm={12}
				md={12}
				className="px-0"
				getIsFieldDisabled={getIsFieldDisabled}
			/>

			<BaseField shouldUpdate noStyle>
				{({ }, { }, { getFieldValue, setFieldValue }) => {
					return (
						<Field
							name="primaryLocation"
							label={t("primary-location")}
							rules={[
								{
									required: !getIsFieldDisabled("primaryLocation"),
									message: t(
										"required-primary-location"
									),
								},
							]}
						>
							<ResourceSelect
								resourcePath="/locations"
								renderLabel={renderSettingsResourceLabel}
								placeholder={t("primary-location")}
								hasSearch
								getSearchFilters={getSettingsResourceSearchFiltersWithLookups}
								disabled={getIsFieldDisabled("primaryLocation")}
								onChange={(value) => {
									let otherLocations = getFieldValue("otherLocations") || [];
									otherLocations = otherLocations.filter((item) => item.id !== value.id);
									setFieldValue("otherLocations", otherLocations);
								}}
							/>
						</Field>
					);
				}}
			</BaseField>

			<BaseField shouldUpdate noStyle>
				{({ }, { }, { getFieldValue }) => {
					const primaryLocation = getFieldValue("primaryLocation");
					let resourcePath = "/locations";
					if (primaryLocation) {
						resourcePath += `?excluded=${primaryLocation.id}`;
					}
					return (
						<Field
							name="otherLocations"
							label={t("other-locations")}
						>
							<ResourceSelect
								mode="multiple"
								resourcePath={resourcePath}
								renderLabel={renderSettingsResourceLabel}
								placeholder={t("other-locations")}
								hasSearch
								getSearchFilters={getSettingsResourceSearchFiltersWithLookups}
								disabled={getIsFieldDisabled("otherLocations")}
							/>
						</Field>
					);
				}}
			</BaseField>

			<Field
				name="payGroup"
				label={t("pay-group")}
				rules={[
					{
						required: !getIsFieldDisabled("payGroup"),
						message: t("required-pay-group"),
					},
				]}
			>
				<ResourceSelect
					labelPropName="description"
					resourcePath="/pay-group"
					renderLabel={renderSettingsResourceLabel}
					placeholder={t("pay-group")}
					hasSearch
					getSearchFilters={getSettingsResourceSearchFilters}
					disabled={getIsFieldDisabled("payGroup")}
				/>
			</Field>

			<Field
				name="calculationGroup"
				label={t("calculation-group")}
				rules={[
					{
						required: true,
						message: t("required-calculation-group"),
					},
				]}
			>
				<ResourceSelect
					labelPropName="description"
					resourcePath="/calculation-group"
					renderLabel={renderSettingsResourceLabel}
					placeholder={t("calculation-group")}
					hasSearch
					getSearchFilters={getSettingsResourceSearchFilters}
				/>
			</Field>

			<Field
				name="salaryOrHourly"
				label={t("hourly-or-salary")}
				rules={[
					{
						required: !getIsFieldDisabled("salaryOrHourly"),
						message: t("required-hourly-or-salary"),
					},
				]}
			>
				<CompensationSelect
					placeholder={t("hourly-or-salary")}
					disabled={getIsFieldDisabled("salaryOrHourly")}
				/>
			</Field>

			<div className="d-flex justify-content-end">
				<Button
					className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
					onClick={close}
					disabled={submitting}
				>
					{t("cancel")}
				</Button>

				<Button
					color="primary"
					type="submit"
					loading={submitting}
					className="btn-round btn-icon shadow-none border px-3 btn btn-dark btn-sm"
				>
					{t("save")}
				</Button>
			</div>
		</Form>
	);
}

export default ChangeCompanyInformation;
